<template>
  <div class="search-container">
    <div
      ref="searchbox"
      class="searchbox"
    >
      <searchbar
        :context="'results'"
        @search="search"
      />
    </div>
    <div
      v-disableable="{disabled: $store.getters['session/onTour']}"
      class="filters"
    >
      <button
        v-for="(type, index) in resultTypes"
        :key="type"
        :class="{ selected: filter === type }"
        class="filter"
        @click="searchTab(type)"
      >
        {{ resultTypeLabels[index] }} ({{ totals[type.toLowerCase()] }})
      </button>
      <div class="total">
        {{ totals['all'] }} results
        <span v-if="!!suggestATopicUrl"> | <a
          :href="suggestATopicUrl"
          target="_blank"
          rel="noopener"
          class="suggest-a-topic"
          @click="sendEvent"
        >
          Suggest a topic
        </a>
        </span>
      </div>
    </div>

    <transition name="fade">
      <div
        v-if="show"
        class="search-results"
      >
        <div
          v-if="topics.suggestions && topics.suggestions.length > 0"
          class="did-you-mean"
        >
          Did you mean: <router-link :to="{ name: 'search#index', query: { q: topics.suggestions[0].word } }">
            {{ topics.suggestions[0].word }}
          </router-link>
        </div>
        <div
          v-if="currentFilterResults.total < 1"
          class="empty"
        >
          <p>No results found.</p>
          <p>Modify your search and try again.</p>
          <a
            v-if="!!suggestATopicUrl"
            :href="suggestATopicUrl"
            target="_blank"
            rel="noopener"
            class="suggest-a-topic"
            @click="sendEvent"
          >
            Suggest a topic
          </a>
        </div>
        <ul v-else-if="filter === 'Questions'">
          <question-search-result
            v-for="flashcard in flashcards.records"
            :key="flashcard.id"
            :flashcard="flashcard"
            :flashcard-category-text="flashcardCategoriesText(flashcard)"
            :flashcard-route="flashcardSearch(flashcard)"
            :locked="isLocked(flashcard)"
            @clicked-result="clickedResult"
          />
        </ul>
        <ul v-else-if="filter === 'Multimedia'">
          <multimedia-search-result
            v-for="attachmentId in multimedia.records"
            :key="attachmentId"
            :attachment-id="attachmentId"
            @clicked-result="clickedResult"
          />
        </ul>
        <ul v-else-if="filter === 'Topics'">
          <template v-for="(item, i) in topics.session">
            <template v-if="item.type === 'Topic'">
              <bookmark-tourtip
                v-if="i === 0 && showBookmarkTourtip(2) && $store.getters['session/onTour']"
                :key="item.id"
                :sequence="2"
              >
                <topic-search-result
                  :classes="'tour'"
                  :locked="isLocked(getObject(item))"
                  :root-topic="rootTopic(getObject(item))"
                  :topic="getObject(item)"
                  :topic-search-route="topicSearchRoute(getObject(item))"
                  @clicked-result="clickedResult"
                />
              </bookmark-tourtip>
              <topic-search-result
                v-else
                :key="'tsr' + item.id"
                v-disableable="{blur: true, dim: true, disabled: $store.getters['session/onTour']}"
                :locked="isLocked(getObject(item))"
                :root-topic="rootTopic(getObject(item))"
                :topic="getObject(item)"
                :topic-search-route="topicSearchRoute(getObject(item))"
                @clicked-result="clickedResult"
              />
            </template>
            <category-search-result
              v-if="item.type === 'Category'"
              :key="item.id"
              v-disableable="{blur: true, dim: true, disabled: $store.getters['session/onTour']}"
              :category="getObject(item)"
              @clicked-result="clickedResult"
            />
          </template>
        </ul>

        <search-pagination
          v-if="totalPages(currentFilterResults) > 1"
          v-disableable="{blur: true, dim: true, disabled: $store.getters['session/onTour']}"
          :current-filter-results="currentFilterResults"
          :total-pages="totalPages(currentFilterResults)"
          @next-page="nextPage"
          @previous-page="previousPage"
        />
      </div>
    </transition>
  </div>
</template>

<script>
import BookmarkTourtip from '@components/tour/bookmarks/bookmarkTourtip.vue';
import CategorySearchResult from '@components/search/results/category.vue';
import MultimediaSearchResult from '@components/search/results/multimedia.vue';
import QuestionSearchResult from '@components/search/results/question.vue';
import Searchbar from '@components/singletons/searchbar.vue';
import SearchPagination from '@components/search/pagination.vue';
import TopicSearchResult from '@components/search/results/topic.vue';

import AuthenticationMixin from '@/mixins/authentication';
import GlobalMixin from '@/mixins/global';

import { incrementTourSequence } from '@/utils/tour';

export default {
  components: {
    BookmarkTourtip,
    CategorySearchResult,
    QuestionSearchResult,
    MultimediaSearchResult,
    Searchbar,
    SearchPagination,
    TopicSearchResult,
  },
  mixins: [ AuthenticationMixin, GlobalMixin ],
  data() {
    return {
      selectedFilter: null,
      flashcards: {},
      multimedia: {}, // Attachments
      searchTerm: '',
      show: true,
      topics: {},
    };
  },
  computed: {
    filter() {
      if (this.selectedFilter) return this.selectedFilter;
      else if (this.firstPopulatedType) return this.firstPopulatedType;
      return this.resultTypes[0];
    },
    currentFilterResults() {
      if (this.filter === 'Questions') {
        return this.flashcards;
      } else if (this.filter === 'Multimedia') {
        return this.multimedia;
      } else {
        return this.topics;
      }
    },
    firstPopulatedType() {
      for (let type of this.resultTypes) {
        if (this.totals[type.toLowerCase()].length > 0) return type;
      }
      return null;
    },
    searchTabNamesOrdered() {
      const defaultSearchTabNamesOrdered = [
        {
          'topic_categories': 'Topics',
        },
        {
          'flashcard_categories': 'Questions',
        },
        {
          'attachment_categories': 'Multimedia',
        },
      ];
      const productVar = this.$store.getters['productVars/get']['search_tab_names_ordered']?.value;
      if (productVar) {
        const defaultTypes = defaultSearchTabNamesOrdered.map(type => Object.keys(type)[0]);

        return JSON.parse(productVar)?.ordered_tabs.filter(tab => {
          const tabKey = Object.keys(tab)[0];
          return defaultTypes.includes(tabKey);
        });
      }
      // currently only Mastery Nursing has a value for search_tab_names_ordered so all others use
      // the default order
      return defaultSearchTabNamesOrdered;
    },
    resultTypeLabels() {
      const labels = this.searchTabNamesOrdered.map(tab => Object.values(tab)[0]);
      if(!this.showTab('Questions')) {
        return labels.filter( tabName => tabName !== 'Questions' );
      }
      return labels;
    },
    resultTypes() {
      const keys = this.searchTabNamesOrdered.map(tab => Object.keys(tab)[0]);
      const typeMap = {
        'attachment_categories': 'Multimedia',
        'flashcard_categories': 'Questions',
        'topic_categories': 'Topics',
      };
      const types = keys.map(key => typeMap[key]);
      if (!this.showTab('Questions') ) {
        return types.filter( tabName => tabName !== 'Questions' );
      }
      return types;
    },
    suggestATopicUrl() {
      const pv = this.$store.getters['productVars/get']['suggestURL'];
      return pv?.value;
    },
    topicResults() {
      if (this.topics.session) {
        let topics = this.topics.session.map(id => this.$store.getters['topics/topics'][id]);
        let sortedTopics = topics.sort( (a, b) => this.topics.scores[b.id] - this.topics.scores[a.id] );
        return sortedTopics;
      }
    },
    totals() {
      let questionsTotal = this.flashcards.total || 0;
      let topicsTotal = this.topics.total || 0;
      let multimediaTotal = this.multimedia.total || 0;
      let all = questionsTotal + topicsTotal + multimediaTotal;
      return {
        all: all,
        questions: questionsTotal,
        topics: topicsTotal,
        multimedia: multimediaTotal,
      };
    },
  },
  mounted() {
    this.search(this.searchTerm);
  },
  created() {
    let q = this.$route.query.q;
    if (q) {
      this.searchTerm = q;
    }
    this.$analytics.events.viewedSearchPage();
  },
  methods: {
    clearResults() {
      this.topics = {};
      this.flashcards = {};
      this.multimedia = {};
    },
    clickedResult(asset, type, locked) {
      // let payload = {
      //   type: type,
      //   id: asset.id,
      //   term: this.searchTerm,
      //   position: this.currentFilterResults.page - 1,
      //   total: this.currentFilterResults.total,
      // };
      // This always fails, there is no route
      // this.$store.getters['session/ajax'].post('/searches/record_search', {params: payload}).catch((e) => { });

      this.$analytics.events.viewedSearchResult(type, asset.id);
      if (locked) {
        this.$store.dispatch('ui/toggleUpsellModal', { show: true, source: { type: 'Locked Search Result' }});
      }

      if (type === 'Attachment') {
        this.$router.push({
          name: 'search#attachment',
          params: { attachmentId: asset.id },
          query: this.$route.query });
      }

      if (this.$store.getters['session/onTour']) {
        incrementTourSequence.apply(this, [3]);
      }
    },
    flashcardCategories(flashcard) {
      return flashcard.categories.map(c => {
        return this.$store.getters['categories/categories'][c.id];
      }).filter(c => c);
    },
    flashcardCategoriesText(flashcard) {
      return this.flashcardCategories(flashcard).map(c => c.name).join(', ');
    },
    flashcardSearch(flashcard) {
      if (this.locked) {
        return { query: this.currentQuery };
      }
      return { name: 'search#flashcard', params: { flashcardId: flashcard.id }, query: { q: this.searchTerm } };
    },
    // Gets the display object from the session object
    getObject(obj) {
      /*
        returns the object referenced by the session object
          in the case of the topic it's pretty straight forward the returned object type from the id is returned
          in the case of the category this check ensures that there is a value navigation path in the app
            for this category
      */
      if (obj.type == 'Topic') {
        return this.$store.getters['topics/topics'][obj.id];;
      } else if (obj.type == 'Category') {
        let category = this.$store.getters['categories/categories'][obj.id];
        if(!category) { return category; }
        let topicCategoryTypes = this.$store.getters['categoryTypes/topicCategoryTypes'];
        let topicCategoryTypeIds = topicCategoryTypes.map(elm => elm.id);
        let isValidTopic = topicCategoryTypeIds.includes(category.categoryTypeId);
        if(isValidTopic) {
          return category;
        }
      }
    },
    isLocked(asset) {
      if (asset.title) {
        let topic = this.rootTopic(asset) || asset;
        return !topic.productAssociations.some(pa => this.$access(this.$store, 'category', pa.categoryId));
      } else {
        return !this.flashcardCategories(asset).some(c => this.$access(this.$store, 'category', c.id));
      }
    },
    nextPage(results) {
      this.show = false;
      this.$refs.searchbox.scrollIntoView({behavior: 'smooth'});
      if (this.filter === 'Questions') {
        this.searchFlashcards(results.page + 1);
      } else if (this.filter === 'Multimedia') {
        this.searchMultimedia(results.page + 1);
      } else {
        this.searchTopics(results.page + 1);
      }
    },
    previousPage(results) {
      this.$refs.searchbox.scrollIntoView({behavior: 'smooth'});
      this.show = false;
      if (this.filter === 'Questions') {
        this.searchFlashcards(results.page - 1);
      } else if (this.filter === 'Multimedia') {
        this.searchMultimedia(results.page - 1);
      } else {
        this.searchTopics(results.page - 1);
      }
    },
    rootTopic(topic) {
      if (topic.path.includes('.')) {
        return this.$store.getters['topics/topics'][topic.path.split('.')[0]];
      }
    },
    search(query) {
      this.searchTerm = query;
      if (!this.searchTerm) {
        this.clearResults();
        return;
      }
      this.searchFlashcards();
      this.searchTopics();
      this.searchMultimedia();

      this.$analytics.events.completedSearch(this.searchTerm, this.currentResource, this.$route, this.$store);
    },
    searchFlashcards(page = 1) {
      let results = this.$store.dispatch('search/search', {page: page, q: this.searchTerm, m: 'flashcards'});
      results.then((data) => {
        this.flashcards = data;
        this.show = true;
      });
    },
    searchMultimedia(page = 1) {
      let results = this.$store.dispatch('search/search', {page: page, q: this.searchTerm, m: 'multimedia'});
      results.then((data) => {
        this.$store.commit('attachments/setAttachments', (data.attachments || []));
        this.$store.commit('attachments/setSearchData', (data));
        this.multimedia = data;
        this.show = true;
      });
    },
    searchTab(type) {
      this.selectedFilter = type;
      this.useDefaultFilter = false;
      this.$analytics.events.selectedSearchTab(type);
    },
    searchTopics(page = 1) {
      let results = this.$store.dispatch('search/search', {page: page, q: this.searchTerm, m: 'topics_categories'});
      results.then((data) => {
        this.$store.commit('topics/setTopics', data.records);
        this.topics = data;

        if( !this.topics.session ) { return; }
        let sessionCategories = this.topics.session.filter( elm => elm.type == 'Category' && this.getObject(elm) );
        // using object.assign to unfreeze category from vuex
        let categories = sessionCategories.map( elm => Object.assign({}, this.getObject(elm)) );

        let processedCategories = [];
        for(let i = 0; categories.length > 0; i++) {
          let parentCategories = categories.filter( elm => elm.path.split('.').length == i );
          categories = categories.filter( elm => elm.path.split('.').length != i );

          parentCategories.forEach( function(category) {
            category.descendantCategories = categories.filter( elm => category.descendants.includes(elm.id) );
            categories = categories.filter( elm => !category.descendants.includes(elm.id) );
          });
          processedCategories = processedCategories.concat(parentCategories);
        }

        // Order the descendant categories by their score
        let self = this;
        let score = (elm1) => self.topics.session.find( elm2 => elm1.id == elm2.id ).score;
        processedCategories.forEach( elm => elm.descendantCategories.sort( (a,b) => score(a) - score(b) ) );
        // Trim the session to only use the highest level Categories
        let keepCategoryIds = processedCategories.map( elm => elm.id );
        this.topics.session = this.topics.session.filter( elm => elm.type != 'Category' || (elm.type == 'Category' && keepCategoryIds.includes(Number(elm.id))) );
        this.topics.suggestions = data.suggestions.sort((a, b) => parseInt(b.freq) - parseInt(a.freq));
        this.show = true;
      });
    },
    sendEvent() {
      this.$analytics.events.clickedHyperlink({ source: 'Search', destination: this.suggestATopicUrl });
    },
    showBookmarkTourtip(sequence) {
      return this.$store.getters['session/onTour'] && sequence === this.$store.getters['session/tourSequence'];
    },
    showTab(type) {
      if (type === 'Questions') {
        // return true if there's a flashcard nav item the user can access
        return this.$store.getters['navigationItems/navigationItemsList'].filter(ni => {
          return this.$access(this.$store, 'navigationItem', ni.id);
        }).find(ni => ni.destination === 'flashcard_categories');
      }
    },
    topicSearchRoute(topic) {
      if (this.isLocked(topic)) {
        return { query: { q: this.searchTerm } };
      }

      let route = { name: 'search#topic', params: { topicId: topic.id }, query: { q: this.searchTerm } };
      if (this.rootTopic(topic)) {
        route.params = {
          subtopicId: topic.id,
          topicId: topic.path.split('.')[0],
        };
      } else {
        route.params = {
          topicId: topic.id,
        };
      }

      return route;
    },
    totalPages(results) {
      return Math.ceil(results.total / results.perPage);
    },
  },
};
</script>

<style lang='scss' scoped>
  .search-container {
    padding: rem-calc(32) 0 !important;

    > *:not([data-modal="attachment-modal"]) {
      max-width: rem-calc(768);
      box-sizing: border-box;
      margin: 0 auto;
    }

    @include breakpoint(medium) {
      padding: rem-calc(16) !important;
    }
  }

  .searchbox {
    height: rem-calc(70);
    position: relative;
  }

  .search-results {
    padding-right: rem-calc(16);

    .empty {
      font-size: rem-calc(16);
      margin-top: rem-calc(48);
      text-align: center;

      p {
        margin-top: 0;

        &:first-child {
          font-size: rem-calc(20);
          font-weight: $semi-bold;
          margin-bottom: rem-calc(4);
        }
      }

      .suggest-a-topic {
        color: inherit;
        display: inline-block;
        font-size: rem-calc(14);
        margin-top: rem-calc(32);
        text-decoration: underline;
      }
    }

    ul {
      list-style-type: none;
      margin: 0;
      padding: 0;
    }
  }

  .searchbar {
    position: relative;

    input[type='text'] {
      border: 2px solid #666666;
      border-radius: 22px;
      box-sizing: border-box;
      font-size: 18px;
      font-weight: $semi-bold;
      padding: 10px 22px;
      width: 100%;

      @include focus-not-visible {
        outline: 0;
      }

      &::-ms-clear {
        display: none;
      }
    }

    button {
      background-color: #00d8f8;
      border-radius: 100%;
      height: 32px;
      padding: 8px;
      position: absolute;
      right: 6px;
      top: 6px;
      width: 32px;
    }
  }

  .filters {
    display: flex;
    margin-top: rem-calc(24);
    margin-bottom: rem-calc(24);
    padding: 0 rem-calc(16);
    font-size: rem-calc(14);

    .filter {
      border-bottom: 1px solid transparent;
      color: #333333;
      cursor: pointer;
      margin-right: rem-calc(16);
      padding: 0 0 rem-calc(3);
      background: transparent;

      &:hover {
        border-color: #333333;
      }

      &.selected {
        border-bottom-color: #00d8f8;
        color: #000000;
        font-weight: $bold;
      }
    }

    .total {
      color: #666666;
      font-size: rem-calc(12);
      margin-left: auto;

      .suggest-a-topic {
        color: inherit;
        text-decoration: underline;
      }
    }

    @include breakpoint(small) {
      flex-wrap: wrap;

      .total {
        margin-bottom: rem-calc(16);
        order: 1;
        text-align: center;
        width: 100%;
      }

      .filter {
        border-bottom-color: #e4e4e4;
        flex: 1;
        margin: 0;
        order: 2;
      }
    }
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity .5s;
  }
  .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
  }

  .small {
    display: -webkit-box;
    font-size: 14px;
    margin: 0;
  }
  table {
    border: 1px solid black;
    padding: 5px;
    border-collapse: collapse;
    border-spacing: 0;
  }
  td, th {
    padding-left: 50px;
  }
  tr:nth-child(even) {
    background-color: #f2f2f2
  }
  .did-you-mean {
    color: red;
    margin-bottom: 1.5em;
    margin-top: 1.5em;
  }
</style>
